// src/Header.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { auth } from './config/firebase';
import { useAuth } from './AuthContext';
import './Header.css';
import { FaFacebookF, FaLinkedinIn, FaInstagram, FaEnvelope, FaBars, FaTimes } from 'react-icons/fa';
import { FiShield } from 'react-icons/fi';
import SnapConXIcon from './assets/SnapConX-icon.png';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const checkAdmin = async () => {
      if (!currentUser) {
        setIsAdmin(false);
        return;
      }
      try {
        const db = getFirestore();
        const snap = await getDoc(doc(db, 'users', currentUser.uid));
        setIsAdmin(snap.exists() && snap.data().is_admin === true);
      } catch (err) {
        console.error('Error checking admin status:', err);
        setIsAdmin(false);
      }
    };
    checkAdmin();
  }, [currentUser]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
      closeMobileMenu();
    } catch (error) {
      console.error('Logout error:', error);
      alert('Failed to logout. Please try again.');
    }
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header className={`main-header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="header-container">
        <div className="header-left">
          <Link to="/" className="logo-link" onClick={closeMobileMenu}>
            <img src={SnapConXIcon} alt="SnapConX logo" className="header-logo" />
            <div className="brand-block">
              <div className="brand-name">SnapConX</div>
              <div className="brand-tagline">Connect • Share • Engage</div>
            </div>
          </Link>
        </div>

        <button
          className="mobile-menu-toggle"
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
        </button>

        <nav className={`header-nav ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="nav-links">
            <Link
              to="/"
              className={isActive('/') ? 'active' : ''}
              onClick={closeMobileMenu}
            >
              Home
            </Link>
            <Link
              to="/about"
              className={isActive('/about') ? 'active' : ''}
              onClick={closeMobileMenu}
            >
              About Us
            </Link>
            <Link
              to="/pricing"
              className={isActive('/pricing') ? 'active' : ''}
              onClick={closeMobileMenu}
            >
              Dashboard Packages
            </Link>
            <Link
              to="/guide"
              className={isActive('/guide') ? 'active' : ''}
              onClick={closeMobileMenu}
            >
              Guides
            </Link>

            {isAdmin && (
              <Link
                to="/admin"
                className={`admin-link ${location.pathname.startsWith('/admin') ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                Admin
              </Link>
            )}
            
            {currentUser ? (
              <Link
                to="/dashboard"
                className={`dashboard-link ${isActive('/dashboard') ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                My Dashboard
              </Link>
            ) : (
              <Link
                to="/login"
                className={`dashboard-link ${isActive('/login') ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                Login
              </Link>
            )}
          </div>

          <div className="social-links">
            <a
              href="mailto:info@dotanddash.co.za"
              aria-label="Email"
              className="social-icon email"
            >
              <FaEnvelope />
            </a>
            <a
              href="https://facebook.com/profile.php?id=61575813950330"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="social-icon facebook"
            >
              <FaFacebookF />
            </a>
            <a
              href="https://linkedin.com/company/snapconx"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="social-icon linkedin"
            >
              <FaLinkedinIn />
            </a>
            <a
              href="https://instagram.com/snapconx"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="social-icon instagram"
            >
              <FaInstagram />
            </a>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
